import { Component } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { DATE_FORMAT } from '@app/constants';
import { AnalyticEvents } from '@app/enums';
import { Employee } from '@app/models/employee/employee.model';
import { Applicant } from '@app/models/recruiting/applicant.model';
import { Interview } from '@app/models/recruiting/interview.model';
import { JobPosting } from '@app/models/recruiting/job-posting.model';
import { InterviewAssignAttributes } from '@app/modules/applicant-tracker/interfaces/interviews-assign.interface';
import { InterviewService, ManageInterviewService } from '@app/modules/applicant-tracker/services';
import { AnalyticService, NotifyService } from '@app/services';
import moment from 'moment';

type StringFormGroup = FormGroup<{ [key: string]: FormControl<number[] | null> }>;
type MomentFormGroup = FormGroup<{ [key: string]: FormControl<moment.Moment | null> }>;

interface InterviewFormGroup {
    dueDateGroup: MomentFormGroup;
    interviewersGroup: StringFormGroup;
}

@Component({
    templateUrl: './manage-interview.template.html',
    styleUrls: ['./manage-interview.style.scss'],
})
export class ManageInterviewView {
    selectedApplicants: Applicant[] = [];
    jobPosting: JobPosting;
    formGroup: FormGroup<InterviewFormGroup>;
    interviewers: Employee[] = [];
    interview: Interview;

    isLoading = false;
    minDate: Date = new Date();

    constructor(
        private manageInterviewService: ManageInterviewService,
        private interviewService: InterviewService,
        private route: ActivatedRoute,
        private router: Router,
        private notify: NotifyService,
        private analyticService: AnalyticService
    ) {
        this.selectedApplicants = this.manageInterviewService.selectedApplicants;
        this.jobPosting = this.route.snapshot.data.models?.get('jobPosting');
        this.interview = this.manageInterviewService.interview;

        if (this.jobPosting) {
            this.interviewers = this.jobPosting.managerAssignments.map(
                (managerAssignment) => managerAssignment.employee
            );
        }

        this.createFormGroup();
    }

    get interviewersGroup(): StringFormGroup {
        return this.formGroup.controls.interviewersGroup;
    }

    get dueDateGroup(): MomentFormGroup {
        return this.formGroup.controls.dueDateGroup;
    }

    async onSave(form: FormGroup<InterviewFormGroup>): Promise<void> {
        if (!form.valid) {
            this.notify.error('applicant-tracker.pleaseFillAllRequiredFields');
            return;
        }

        this.isLoading = true;

        try {
            await this.interviewService.assignMultipleApplicantToInterview(this.interview.id, this.createPayload());
            this.analyticService.trackEvent(
                AnalyticEvents.RecruitingJobPostingInterviewsEditInterviewClickCompleteToSave
            );
            this.router.navigate(['../'], { relativeTo: this.route });
            this.isLoading = false;
        } catch (err) {
            this.notify.error(err);
        }
    }

    private createPayload(): InterviewAssignAttributes[] {
        const dueDateGroup = this.dueDateGroup;
        const interviewersGroup = this.interviewersGroup;
        let data: InterviewAssignAttributes[] = [];

        data = this.selectedApplicants.map((applicant: Applicant) => {
            return {
                attributes: {
                    attach: {
                        interviewers: interviewersGroup.value[applicant.id],
                    },
                    feedback_due_date: dueDateGroup.value[applicant.id].format(DATE_FORMAT),
                    applicant_id: applicant.id,
                },
            };
        });

        return data;
    }

    private createFormGroup(): void {
        const dueDateGroup = {};
        const interviewersGroup = {};

        this.selectedApplicants.forEach((applicant: Applicant) => {
            dueDateGroup[applicant.id] = new FormControl('', [Validators.required]);
            interviewersGroup[applicant.id] = new FormControl('', [Validators.required]);
        });

        this.formGroup = new FormGroup({
            dueDateGroup: new FormGroup({ ...dueDateGroup }),
            interviewersGroup: new FormGroup({ ...interviewersGroup }),
        });
    }
}
